import TomSelect from "tom-select";
import "tom-select/dist/css/tom-select.css";
import { ApiService } from "../services/ApiService.js";

/**
 * ============================
 *  PRIVATE HELPERS
 * ============================
 */

function _qs(root, selector, name) {
	const el = root.querySelector(selector);
	if (!el) throw new Error(`VehicleSelectModule: no se encontró ${name} (${selector})`);
	if (el.tagName !== "SELECT") throw new Error(`VehicleSelectModule: ${name} no es SELECT (${el.tagName})`);
	return el;
}

function _destroyIfExists(el) {
	if (el?.tomselect) {
		el.tomselect.destroy();
		el.tomselect = null;
	}
}

function _createTom(el, placeholder) {
	return new TomSelect(el, {
		create: false,
		placeholder,
		closeAfterSelect: true,
		maxOptions: 5000,
		searchField: ["text"],
		hidePlaceholder: true,

		

		render: {
			option: function (data, escape) {
				if (data.value === "") return "";

				const img = data.logo
					? `<img src="${escape(data.logo)}" class="ts-logo" loading="lazy" />`
					: "";

				return `
          <div class="d-flex align-items-center gap-2 py-1">
            ${img}
            <span>${escape(data.text)}</span>
          </div>
        `;
			},

			item: function (data, escape) {
				const img = data.logo
					? `<img src="${escape(data.logo)}" class="ts-logo" loading="lazy" />`
					: "";

				return `
          <div class="d-flex align-items-center gap-2">
            ${img}
            <span>${escape(data.text)}</span>
          </div>
        `;
			},

			no_results: function () {
				return `<div class="no-results p-2 text-muted">Sin resultados</div>`;
			},
		},
	});
}

function _setDisabled(selectEl, disabled) {
	if (selectEl?.tomselect) {
		if (disabled) selectEl.tomselect.disable();
		else selectEl.tomselect.enable();
	} else {
		selectEl.disabled = disabled;
	}
}

function _mapToOptions(arr, { withLogo = false } = {}) {
	return (arr || []).map(x => ({
		value: String(x.id),
		text: x.name,
		...(withLogo ? { logo: x.logo } : {}),
	}));
}

/**
 * ============================
 *  PRIVATE API (encapsulada)
 * ============================
 */

async function _getMarcas() {
	const response = await ApiService.post("Vehiculosapi/marcas");
	if (response.status !== 0) return [];

	return response.message.map(item => ({
		id: item.id,
		name: item.nombre,
		// si tu logo real es por URL fija:
		// logo: `https://app.com/${item.id}.webp`,
		// si viene desde API:
		logo: item.logo,
	}));
}

async function _getModelosByMarca(marcaId) {
	const response = await ApiService.post(`Vehiculosapi/modelos?IdMarca=${marcaId}`);
	if (response.status !== 0) return [];

	return response.message.map(item => ({
		id: item.id,
		name: item.nombre,
	}));
}

async function _getVehiculos(marcaId, modeloId) {
	const response = await ApiService.post(`Vehiculosapi/vehiculos?IdMarca=${marcaId}&IdModelo=${modeloId}`);
	if (response.status !== 0) return [];

	return response.message.map(item => ({
		id: item.id,
		name: item.descripcion,
	}));
}

/**
 * ============================
 *  PRIVATE LOGIC (cascada)
 * ============================
 */

async function _loadMarcas(tomMarca) {
	const marcas = await _getMarcas();
	tomMarca.clearOptions();
	tomMarca.addOptions(_mapToOptions(marcas, { withLogo: true }));
	tomMarca.refreshOptions(false);
}

async function _loadModelos(tomModelo, marcaId) {
	const modelos = await _getModelosByMarca(marcaId);
	tomModelo.clear(true);
	tomModelo.clearOptions();
	tomModelo.addOptions(_mapToOptions(modelos));
	tomModelo.refreshOptions(false);
}

async function _loadVehiculos(tomVehiculo, marcaId, modeloId) {
	const vehiculos = await _getVehiculos(marcaId, modeloId);
	tomVehiculo.clear(true);
	tomVehiculo.clearOptions();
	tomVehiculo.addOptions(_mapToOptions(vehiculos));
	tomVehiculo.refreshOptions(false);
}

/**
 * ============================
 *  PUBLIC MODULE
 * ============================
 */

export const VehicleSelectModule = {
	/**
	 * Mount corto: arma todo y devuelve handlers
	 */
	mount: async ({
		root = document,
		selectMarca,
		selectModelo,
		selectVehiculo,

		placeholderMarca = "Marca",
		placeholderModelo = "Modelo",
		placeholderVehiculo = "Versión",

		initial = { marcaId: null, modeloId: null, vehiculoId: null },

		onChange = { marca: null, modelo: null, vehiculo: null },
	}) => {
		const $marca = _qs(root, selectMarca, "selectMarca");
		const $modelo = _qs(root, selectModelo, "selectModelo");
		const $vehiculo = _qs(root, selectVehiculo, "selectVehiculo");

		// destruir si estaban
		_destroyIfExists($marca);
		_destroyIfExists($modelo);
		_destroyIfExists($vehiculo);

		// crear
		const tomMarca = _createTom($marca, placeholderMarca);
		const tomModelo = _createTom($modelo, placeholderModelo);
		const tomVehiculo = _createTom($vehiculo, placeholderVehiculo);

		// estado inicial
		tomModelo.disable();
		tomVehiculo.disable();

		// cargar marcas
		await _loadMarcas(tomMarca);

		// handlers (definidos acá pero cortos)
		const handleMarcaChange = async (marcaId) => {
			_setDisabled($modelo, !marcaId);
			_setDisabled($vehiculo, true);

			tomVehiculo.clear(true);
			tomVehiculo.clearOptions();

			if (!marcaId) {
				if (onChange?.marca) onChange.marca(null);
				return;
			}

			await _loadModelos(tomModelo, marcaId);

			if (onChange?.marca) onChange.marca(marcaId);
		};

		const handleModeloChange = async (modeloId) => {
			const marcaId = tomMarca.getValue();

			_setDisabled($vehiculo, !modeloId);

			if (!modeloId) {
				tomVehiculo.clear(true);
				tomVehiculo.clearOptions();
				if (onChange?.modelo) onChange.modelo(null);
				return;
			}

			await _loadVehiculos(tomVehiculo, marcaId, modeloId);

			if (onChange?.modelo) onChange.modelo(modeloId);
		};

		const handleVehiculoChange = (vehiculoId) => {
			if (onChange?.vehiculo) onChange.vehiculo(vehiculoId || null);
		};

		// eventos
		tomMarca.on("change", handleMarcaChange);
		tomModelo.on("change", handleModeloChange);
		tomVehiculo.on("change", handleVehiculoChange);

		// precarga
		if (initial?.marcaId) {
			tomMarca.setValue(String(initial.marcaId), true);
			await handleMarcaChange(String(initial.marcaId));

			if (initial?.modeloId) {
				tomModelo.setValue(String(initial.modeloId), true);
				await handleModeloChange(String(initial.modeloId));

				if (initial?.vehiculoId) {
					tomVehiculo.setValue(String(initial.vehiculoId), true);
				}
			}
		}

		return {
			tomMarca,
			tomModelo,
			tomVehiculo,
			getValue: () => ({
				marcaId: tomMarca.getValue() || null,
				modeloId: tomModelo.getValue() || null,
				vehiculoId: tomVehiculo.getValue() || null,
			}),
			destroy: () => {
				tomMarca.destroy();
				tomModelo.destroy();
				tomVehiculo.destroy();
			},
		};
	},
};
